import React, { useState, useCallback } from 'react'
import Cropper from 'react-easy-crop'
import { X, ZoomIn, ZoomOut, Check, RotateCw } from 'lucide-react'
import getCroppedImg from '../services/cropImage'

interface ImageCropperModalProps {
    image: string
    onClose: () => void
    onCropComplete: (croppedFile: File) => void
    aspect?: number
}

const ImageCropperModal = ({
    image,
    onClose,
    onCropComplete,
    aspect
}: ImageCropperModalProps) => {
    const [crop, setCrop] = useState({ x: 0, y: 0 })
    const [zoom, setZoom] = useState(1)
    const [rotation, setRotation] = useState(0)
    const [croppedAreaPixels, setCroppedAreaPixels] = useState<any>(null)
    const [loading, setLoading] = useState(false)
    const [currentAspect, setCurrentAspect] = useState<number | undefined>(aspect)

    const onCropChange = (crop: any) => {
        setCrop(crop)
    }

    const onZoomChange = (zoom: number) => {
        setZoom(zoom)
    }

    const onRotationChange = (rotation: number) => {
        setRotation(rotation)
    }

    const onCropAreaComplete = useCallback((_croppedArea: any, croppedAreaPixels: any) => {
        setCroppedAreaPixels(croppedAreaPixels)
    }, [])

    const handleSave = async () => {
        try {
            setLoading(true)
            const croppedBlob = await getCroppedImg(image, croppedAreaPixels, rotation)
            if (croppedBlob) {
                const file = new File([croppedBlob], 'cropped_image.jpg', { type: 'image/jpeg' })
                onCropComplete(file)
            }
        } catch (e) {
            console.error(e)
        } finally {
            setLoading(false)
        }
    }

    return (
        <div className="fixed inset-0 z-[100] bg-black/90 flex flex-col animate-fade-in">
            {/* Header */}
            <div className="p-4 flex justify-between items-center bg-black/50 backdrop-blur-md border-b border-white/10 shrink-0">
                <div className="text-white">
                    <h3 className="font-bold">Ajustar Imagem</h3>
                    <div className="flex gap-2 mt-1">
                        <button
                            onClick={() => setCurrentAspect(1)}
                            className={`px-2 py-0.5 text-[9px] font-bold rounded border transition-all ${currentAspect === 1 ? 'bg-brand-500 border-brand-500 text-white' : 'bg-transparent border-white/20 text-gray-400'}`}
                        >
                            1:1 (Quadrado)
                        </button>
                        <button
                            onClick={() => setCurrentAspect(4 / 5)}
                            className={`px-2 py-0.5 text-[9px] font-bold rounded border transition-all ${currentAspect === 4 / 5 ? 'bg-brand-500 border-brand-500 text-white' : 'bg-transparent border-white/20 text-gray-400'}`}
                        >
                            4:5 (Retrato)
                        </button>
                        <button
                            onClick={() => setCurrentAspect(undefined)}
                            className={`px-2 py-0.5 text-[9px] font-bold rounded border transition-all ${currentAspect === undefined ? 'bg-brand-500 border-brand-500 text-white' : 'bg-transparent border-white/20 text-gray-400'}`}
                        >
                            Livre (Original)
                        </button>
                    </div>
                </div>
                <button
                    onClick={onClose}
                    className="p-2 text-gray-400 hover:text-white transition-colors"
                >
                    <X size={24} />
                </button>
            </div>

            {/* Cropper Container */}
            <div className="relative flex-1 bg-[#121212]">
                <Cropper
                    image={image}
                    crop={crop}
                    zoom={zoom}
                    rotation={rotation}
                    aspect={currentAspect}
                    onCropChange={onCropChange}
                    onCropComplete={onCropAreaComplete}
                    onZoomChange={onZoomChange}
                    onRotationChange={onRotationChange}
                    showGrid={true}
                    style={{
                        containerStyle: {
                            width: '100%',
                            height: '100%',
                            backgroundColor: '#121212'
                        }
                    }}
                />
            </div>

            {/* Controls */}
            <div className="p-6 bg-black/50 backdrop-blur-md border-t border-white/10 shrink-0 flex flex-col gap-6">
                <div className="flex flex-col gap-4">
                    <div className="flex items-center gap-4 text-white">
                        <ZoomOut size={18} className="text-gray-400" />
                        <input
                            type="range"
                            value={zoom}
                            min={1}
                            max={3}
                            step={0.1}
                            aria-labelledby="Zoom"
                            onChange={(e) => setZoom(Number(e.target.value))}
                            className="flex-1 accent-brand-500 h-1.5 bg-white/20 rounded-full appearance-none cursor-pointer"
                        />
                        <ZoomIn size={18} className="text-gray-400" />
                    </div>

                    <div className="flex items-center gap-4 text-white">
                        <RotateCw size={18} className="text-gray-400" />
                        <input
                            type="range"
                            value={rotation}
                            min={0}
                            max={360}
                            step={1}
                            aria-labelledby="Rotation"
                            onChange={(e) => setRotation(Number(e.target.value))}
                            className="flex-1 accent-orange-500 h-1.5 bg-white/20 rounded-full appearance-none cursor-pointer"
                        />
                        <span className="text-[10px] font-mono text-gray-400 w-8">{rotation}°</span>
                    </div>
                </div>

                <div className="flex gap-4">
                    <button
                        onClick={onClose}
                        className="flex-1 py-3 px-6 rounded-2xl bg-white/10 text-white font-bold hover:bg-white/20 transition-all border border-white/5 active:scale-95"
                    >
                        Cancelar
                    </button>
                    <button
                        disabled={loading}
                        onClick={handleSave}
                        className="flex-1 py-3 px-6 rounded-2xl bg-brand-600 text-white font-black shadow-xl shadow-brand-600/20 hover:bg-brand-700 transition-all flex items-center justify-center gap-2 active:scale-95 disabled:opacity-50"
                    >
                        {loading ? 'Salvando...' : <><Check size={20} /> Confirmar Corte</>}
                    </button>
                </div>
            </div>
        </div>
    )
}

export default ImageCropperModal
